import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Users, CalendarClock, Trophy, FolderKanban, DollarSign, ArrowUpRight, TrendingUp,
    AlertTriangle, Mail, CheckCircle2, Clock, Sparkles, Users2, FilePlus,
    FileClock, UserPlus, Hourglass, Rocket, RefreshCw, Zap, ChevronRight, X,
    SkipForward, Send, BellRing,
} from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds/crm";
import { uniqueFollowups } from "@/lib/notificationUtils";
import { normalizeListResponse } from "@/lib/listResponse";

const STAGE_LABELS = {
    new_lead: "New", contacted: "Contacted", follow_up: "Follow-up",
    proposal_sent: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost",
};

const TASK_ICON = {
    "calendar-clock": CalendarClock,
    "alert-triangle": AlertTriangle,
    "users-2": Users2,
    "file-plus": FilePlus,
    "file-clock": FileClock,
    "user-plus": UserPlus,
    "hourglass": Hourglass,
    "rocket": Rocket,
};
const TASK_TONE = {
    primary: "bg-primary/10 text-primary",
    warning: "bg-warning/15 text-warning",
    destructive: "bg-destructive/15 text-destructive",
    success: "bg-success/15 text-success",
};

const PRIORITY_STYLE = {
    urgent: "bg-destructive/10 text-destructive border-destructive/40",
    high: "bg-warning/10 text-warning border-warning/40",
    medium: "bg-primary/10 text-primary border-primary/40",
    low: "bg-muted text-muted-foreground border-border",
};

const FILTERS = [
    { v: "today", label: "Today" },
    { v: "tomorrow", label: "Tomorrow" },
    { v: "week", label: "This Week" },
    { v: "month", label: "This Month" },
];

const PIPELINE_FILTERS = [
    { v: "today", label: "Today" },
    { v: "week", label: "This Week" },
    { v: "month", label: "This Month" },
    { v: "all", label: "All Time" },
];

const EMPTY_STATS = {
    pipeline: {},
    monthly_leads: [],
    total_leads: 0,
    active_leads: 0,
    pending_followups: 0,
    upcoming_followups: 0,
    won_leads: 0,
    active_projects: 0,
    total_projects: 0,
    revenue: 0,
};

const EMPTY_TODAY = {
    followups: [],
    overdue: [],
    tasks: [],
    counts: { today: 0, overdue: 0 },
};

function StatCard({ label, value, sub, icon: Icon, testId, accent }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card relative border border-border bg-card rounded-md p-5 flex flex-col gap-3"
            data-testid={testId}
        >
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
                <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent || "bg-primary/10 text-primary"}`}>
                    <Icon className="h-4 w-4" />
                </div>
            </div>
            <div className="font-heading text-3xl font-bold tracking-tight tabular-nums">{value}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </motion.div>
    );
}

function FollowupRow({ fu, onComplete, onReschedule, onEmail, onSkip, overdue }) {
    const daysSince = fu.days_since_last_contact;
    const dt = new Date(fu.date);
    const isOverdue = overdue || new Date(fu.date) < new Date();
    return (
        <div
            data-testid={DASHBOARD.followupRow}
            className={`grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors ${
                isOverdue && overdue ? "bg-destructive/5" : ""
            }`}
        >
            <div className="col-span-4 flex items-center gap-2 min-w-0">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                    isOverdue && overdue ? "bg-destructive text-white" : "bg-primary/10 text-primary"
                }`}>#{fu.sequence_number || 1}</span>
                <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{fu.stage_name || fu.type}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{fu.type}</div>
                </div>
            </div>
            <div className="col-span-3">
                <div className="text-[11px] text-muted-foreground">Last contact</div>
                <div className="text-xs font-mono">
                    {fu.last_contact ? new Date(fu.last_contact).toLocaleDateString() : "—"}
                    {daysSince != null && <span className="ml-1 text-muted-foreground">({daysSince}d)</span>}
                </div>
            </div>
            <div className="col-span-2">
                <div className="text-[11px] text-muted-foreground">Due</div>
                <div className={`text-xs font-mono ${isOverdue && overdue ? "text-destructive font-semibold" : ""}`}>
                    {dt.toLocaleDateString()}
                </div>
            </div>
            <div className="col-span-3 flex items-center justify-end gap-1">
                <button
                    onClick={() => onComplete(fu)}
                    data-testid={DASHBOARD.actionComplete}
                    className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs bg-success/10 text-success hover:bg-success/20 transition-colors"
                    title="Mark Complete"
                >
                    <CheckCircle2 className="h-3 w-3" /> Done
                </button>
                <button
                    onClick={() => onSkip(fu)}
                    data-testid={DASHBOARD.actionSkip}
                    className="inline-flex items-center h-7 w-7 justify-center rounded text-xs text-warning hover:bg-warning/10 transition-colors"
                    title="Skip"
                >
                    <SkipForward className="h-3 w-3" />
                </button>
                <button
                    onClick={() => onReschedule(fu)}
                    data-testid={DASHBOARD.actionReschedule}
                    className="inline-flex items-center h-7 w-7 justify-center rounded text-xs hover:bg-secondary transition-colors"
                    title="Reschedule"
                >
                    <RefreshCw className="h-3 w-3" />
                </button>
            </div>
        </div>
    );
}

function EmailDialog({ fu, onClose, onSent }) {
    const [subject, setSubject] = useState(fu.email_subject || `Follow-up — ${fu.lead?.company || ""}`);
    const [body, setBody] = useState(fu.email_body || "");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/followups/${fu.id}/send-email`, { subject, body });
            toast.success(`Email sent to ${fu.lead?.email}`);
            onSent();
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to send email";
            if (String(detail).toLowerCase().includes("follow-up not found")) {
                toast.error("Follow-up not found. Please refresh the page and try again.");
            } else {
                toast.error(detail);
            }
        } finally {
            setBusy(false);
        }
    };
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 12, opacity: 0 }}
                data-testid={DASHBOARD.emailDialog}
                className="w-full max-w-2xl bg-card border border-border rounded-md shadow-xl"
            >
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <h3 className="font-heading text-xl font-semibold">Send follow-up email</h3>
                        <div className="text-xs text-muted-foreground mt-1">
                            To: <span className="font-mono">{fu.lead?.email}</span> · Stage: {fu.stage_name}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Subject</div>
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            required
                            data-testid={DASHBOARD.emailDialogSubject}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        />
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Message</div>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={10}
                            required
                            data-testid={DASHBOARD.emailDialogBody}
                            className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm font-mono leading-relaxed"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm transition-colors">Cancel</button>
                    <button disabled={busy} data-testid={DASHBOARD.emailDialogSend} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 inline-flex items-center gap-1.5">
                        <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send"}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}

function ActivityRow({ a }) {
    const dt = new Date(a.created_at);
    return (
        <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
            <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-semibold shrink-0">
                {(a.actor_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm">
                    <span className="font-medium">{a.actor_name}</span>{" "}
                    <span className="text-muted-foreground">{a.description}</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                    {dt.toLocaleDateString()} · {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground border border-border rounded px-1.5 py-0.5">
                {a.entity_type}
            </span>
        </div>
    );
}

function RescheduleDialog({ fu, onClose, onDone }) {
    const [date, setDate] = useState(new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16));
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/followups/${fu.id}/reschedule`, {
                date: new Date(date).toISOString(),
                reason,
            });
            toast.success("Follow-up rescheduled");
            onDone();
        } catch { toast.error("Failed to reschedule"); }
        setBusy(false);
    };
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <motion.form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 12, opacity: 0 }}
                className="w-full max-w-md bg-card border border-border rounded-md shadow-xl"
            >
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading text-xl font-semibold">Reschedule follow-up</h3>
                    <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">New date &amp; time</div>
                        <input
                            type="datetime-local"
                            required
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            data-testid={DASHBOARD.rescheduleDate}
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                        />
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Reason (optional)</div>
                        <input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Client requested..."
                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 p-4 border-t border-border">
                    <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-border hover:bg-secondary text-sm transition-colors">Cancel</button>
                    <button disabled={busy} data-testid={DASHBOARD.rescheduleSubmit} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                        {busy ? "Saving…" : "Reschedule"}
                    </button>
                </div>
            </motion.form>
        </motion.div>
    );
}

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [today, setToday] = useState(null);
    const [leadItems, setLeadItems] = useState([]);
    const [recs, setRecs] = useState(null);
    const [recsLoading, setRecsLoading] = useState(false);
    const [dashboardTab, setDashboardTab] = useState("followups");
    const [followupFilter, setFollowupFilter] = useState("today");
    const [pipelineFilter, setPipelineFilter] = useState("today");
    const [priority, setPriority] = useState("");
    const [assignedTo, setAssignedTo] = useState("");
    const [users, setUsers] = useState([]);
    const [rescheduleFu, setRescheduleFu] = useState(null);
    const [emailFu, setEmailFu] = useState(null);
    const [notifPermission, setNotifPermission] = useState(
        typeof Notification !== "undefined" ? Notification.permission : "default"
    );
    const notifiedIdsRef = useRef(new Set());

    const loadToday = useCallback(async () => {
        const params = new URLSearchParams({ filter: followupFilter });
        if (priority) params.set("priority", priority);
        if (assignedTo) params.set("assigned_to", assignedTo);
        try {
            const [todayRes, bucketsRes] = await Promise.all([
                api.get(`/dashboard/today?${params}`),
                api.get("/dashboard/followups"),
            ]);
            const todayData = todayRes.data || {};
            const bucketData = bucketsRes.data || {};
            setToday({
                ...EMPTY_TODAY,
                ...todayData,
                counts: {
                    ...EMPTY_TODAY.counts,
                    ...(todayData?.counts || {}),
                    upcoming: bucketData?.counts?.upcoming || 0,
                },
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load today's dashboard");
            setToday(EMPTY_TODAY);
        }
    }, [followupFilter, priority, assignedTo]);

    const loadLeads = useCallback(async () => {
        try {
            const { data } = await api.get("/leads?limit=500");
            setLeadItems(Array.isArray(data.items) ? data.items : []);
        } catch {
            setLeadItems([]);
        }
    }, []);

    const loadRecs = useCallback(async () => {
        setRecsLoading(true);
        try {
            const { data } = await api.get("/dashboard/recommendations");
            setRecs(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load recommendations");
            setRecs(null);
        } finally { setRecsLoading(false); }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, a, u] = await Promise.all([
                api.get("/dashboard/stats"),
                api.get("/activities?limit=8"),
                api.get("/users"),
                ]);
                if (cancelled) return;
                setStats({ ...EMPTY_STATS, ...(s.data || {}) });
                setActivities(Array.isArray(a.data) ? a.data : []);
                setUsers(normalizeListResponse(u.data).items);
            } catch (err) {
                if (cancelled) return;
                toast.error(err.response?.data?.detail || err.message || "Failed to load dashboard");
                setStats(EMPTY_STATS);
                setActivities([]);
                setUsers([]);
            }
        })();
        loadRecs();
        loadLeads();
        return () => { cancelled = true; };
    }, [loadRecs, loadLeads]);

    useEffect(() => { loadToday(); }, [loadToday]);

    // Browser notifications for follow-ups due within the next 5 minutes
    useEffect(() => {
        if (!today || notifPermission !== "granted" || typeof Notification === "undefined") return;
        const soon = Date.now() + 5 * 60 * 1000;
        const now = Date.now();
        uniqueFollowups(today.followups || []).forEach((fu) => {
            const t = new Date(fu.date).getTime();
            if (t <= soon && t >= now - 60_000 && !notifiedIdsRef.current.has(fu.id)) {
                notifiedIdsRef.current.add(fu.id);
                try {
                    new Notification(`${fu.stage_name || "Follow-up"} — ${fu.lead?.company || ""}`, {
                        body: `${fu.lead?.client_name || ""} · ${fu.lead?.email || ""} · due ${new Date(fu.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
                        tag: `fu-${fu.id}`,
                    });
                } catch { /* noop */ }
                toast(`${fu.stage_name || "Follow-up"} due for ${fu.lead?.company}`, {
                    description: `${fu.lead?.email || ""} · ${new Date(fu.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
                    icon: <BellRing className="h-4 w-4 text-primary" />,
                });
            }
        });
        // Overdue toast (one-time)
        uniqueFollowups(today.overdue || []).forEach((fu) => {
            if (!notifiedIdsRef.current.has(`ov-${fu.id}`)) {
                notifiedIdsRef.current.add(`ov-${fu.id}`);
                toast.warning(`Overdue: ${fu.lead?.company || "Lead"}`, {
                    description: `${fu.stage_name || "Follow-up"} · scheduled ${new Date(fu.date).toLocaleDateString()}`,
                });
            }
        });
    }, [today, notifPermission]);

    const requestNotifPermission = async () => {
        if (typeof Notification === "undefined") {
            toast.error("Browser notifications not supported");
            return;
        }
        const p = await Notification.requestPermission();
        setNotifPermission(p);
        if (p === "granted") toast.success("Browser notifications enabled");
        else toast("Notifications disabled");
    };

    if (!stats || !today) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;

    const allTimePipelineData = Object.entries(stats.pipeline).map(([k, v]) => ({ stage: STAGE_LABELS[k] || k, count: v }));

    const complete = async (fu) => {
        try {
            await api.patch(`/followups/${fu.id}`, { status: "completed" });
            toast.success("Marked complete");
            loadToday();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to mark complete");
        }
    };
    const email = (fu) => setEmailFu(fu);
    const skip = async (fu) => {
        if (!window.confirm(`Skip ${fu.stage_name || "this follow-up"}? Subsequent follow-ups will be shifted.`)) return;
        try {
            await api.post(`/followups/${fu.id}/skip`, { shift_subsequent: true });
            toast.success("Follow-up skipped");
            loadToday();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to skip");
        }
    };
    const view = (fu) => {
        window.location.href = `/leads?lead=${fu.lead_id}`;
    };

    const displayedFollowups = followupFilter === "overdue"
        ? today.overdue
        : today.followups;

    const pipelineCounts = PIPELINE_FILTERS.reduce((acc, item) => {
        const now = new Date();
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        let windowStart = new Date(0);
        let windowEnd = new Date(now);
        if (item.v === "today") {
            windowStart = start;
            windowEnd = new Date(start.getTime() + 24 * 60 * 60 * 1000);
        } else if (item.v === "week") {
            windowStart = start;
            windowEnd = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
        } else if (item.v === "month") {
            windowStart = new Date(start.getFullYear(), start.getMonth(), 1);
            windowEnd = new Date(start.getFullYear(), start.getMonth() + 1, 1);
        } else {
            windowStart = new Date(0);
            windowEnd = new Date(now.getFullYear() + 10, 0, 1);
        }
        acc[item.v] = leadItems.reduce((counts, lead) => {
            const created = new Date(lead.created_at || lead.createdAt || now);
            if (created >= windowStart && created < windowEnd) {
                counts[lead.status] = (counts[lead.status] || 0) + 1;
            }
            return counts;
        }, {});
        return acc;
    }, {});
    const pipelineCountsWindow = PIPELINE_FILTERS.find((f) => f.v === pipelineFilter)?.v ? pipelineCounts[pipelineFilter] || {} : {};

    return (
        <div className="space-y-6 animate-fade-in" data-testid={DASHBOARD.root}>
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Today's Focus</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Sales Command Center</h1>
                    <p className="text-sm text-muted-foreground mt-2">Live snapshot of your pipeline, tasks and follow-ups.</p>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    {notifPermission !== "granted" && (
                        <button
                            onClick={requestNotifPermission}
                            data-testid={DASHBOARD.notifEnable}
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-primary/40 bg-primary/5 text-primary text-xs font-medium hover:bg-primary/10 transition-colors"
                            title="Enable browser notifications"
                        >
                            <BellRing className="h-3.5 w-3.5" />
                            Enable notifications
                        </button>
                    )}
                    <TrendingUp className="h-4 w-4 text-success" />
                    <span className="text-muted-foreground">Updated just now</span>
                </div>
            </div>

            <div className="rounded-md border border-border bg-card p-2">
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => setDashboardTab("followups")}
                        className={`h-11 rounded-md text-sm font-medium transition-colors ${
                            dashboardTab === "followups"
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "bg-transparent text-muted-foreground hover:bg-secondary"
                        }`}
                    >
                        Followups & Tasks
                    </button>
                    <button
                        type="button"
                        onClick={() => setDashboardTab("leads")}
                        className={`h-11 rounded-md text-sm font-medium transition-colors ${
                            dashboardTab === "leads"
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "bg-transparent text-muted-foreground hover:bg-secondary"
                        }`}
                    >
                        Leads
                    </button>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {dashboardTab === "followups" ? (
                    <motion.div
                        key="followups"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="border border-border rounded-md bg-card overflow-hidden"
                        data-testid={DASHBOARD.todayFollowups}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-4 flex-wrap">
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                                    <Zap className="h-5 w-5" strokeWidth={2.5} />
                                </div>
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Follow-up Dashboard</div>
                                    <h3 className="font-heading text-xl font-semibold">
                                        {followupFilter === "today" && "Today"}
                                        {followupFilter === "tomorrow" && "Tomorrow"}
                                        {followupFilter === "week" && "This Week"}
                                        {followupFilter === "month" && "This Month"}
                                    </h3>
                                </div>
                            </div>
                            <select value={followupFilter} onChange={(e) => setFollowupFilter(e.target.value)} className="h-9 px-3 rounded-md border border-input bg-card text-xs">
                                {FILTERS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-3 gap-3 p-5 border-b border-border">
                            <div className="border border-border rounded-md p-3">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Today's Follow-ups</div>
                                <div className="font-heading text-2xl font-bold mt-1">{today.counts.today || 0}</div>
                            </div>
                            <div className="border border-border rounded-md p-3">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Overdue Follow-ups</div>
                                <div className="font-heading text-2xl font-bold mt-1 text-destructive">{today.counts.overdue || 0}</div>
                            </div>
                            <div className="border border-border rounded-md p-3">
                                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Upcoming Follow-ups</div>
                                <div className="font-heading text-2xl font-bold mt-1">{today.counts.upcoming || 0}</div>
                            </div>
                        </div>
                        <div className="px-5 py-3 border-b border-border bg-secondary/20 flex flex-wrap items-center gap-2">
                            <div className="ml-auto flex items-center gap-2">
                                <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid={DASHBOARD.filterPriority} className="h-8 px-2 rounded-md border border-input bg-card text-xs">
                                    <option value="">All priorities</option>
                                    {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                                <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} data-testid={DASHBOARD.filterAssigned} className="h-8 px-2 rounded-md border border-input bg-card text-xs">
                                    <option value="">All users</option>
                                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                        </div>
                        {displayedFollowups.length === 0 ? (
                            <div className="text-sm text-muted-foreground py-12 text-center">Nothing scheduled for this window.</div>
                        ) : (
                            <div>
                                {displayedFollowups.map((fu) => (
                                    <FollowupRow
                                        key={fu.id}
                                        fu={fu}
                                        overdue={false}
                                        onComplete={complete}
                                        onReschedule={setRescheduleFu}
                                        onView={view}
                                        onEmail={email}
                                        onSkip={skip}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="border-t border-border px-5 py-4" data-testid={DASHBOARD.todayTasks}>
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Daily agenda</div>
                                    <h3 className="font-heading text-xl font-semibold mt-0.5">Today's Tasks</h3>
                                </div>
                                <span className="text-xs text-muted-foreground">Auto-refreshed</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {today.tasks.map((t) => {
                                    const Icon = TASK_ICON[t.icon] || Clock;
                                    return (
                                        <div
                                            key={t.key}
                                            data-testid={`${DASHBOARD.taskPrefix}${t.key}`}
                                            className={`border border-border rounded-md p-3 flex flex-col gap-2 hover:border-primary/50 hover:-translate-y-[1px] transition-colors transition-transform ${
                                                t.count > 0 && t.tone === "destructive" ? "border-destructive/40 bg-destructive/5" : ""
                                            }`}
                                        >
                                            <div className={`h-8 w-8 rounded-md flex items-center justify-center ${TASK_TONE[t.tone] || TASK_TONE.primary}`}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="font-heading text-2xl font-bold tabular-nums leading-none">{t.count}</div>
                                            <div className="text-[11px] text-muted-foreground leading-tight">{t.label}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="leads"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="border border-border rounded-md bg-card overflow-hidden"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                            <div>
                                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Leads</div>
                                <h3 className="font-heading text-xl font-semibold">Pipeline summary</h3>
                            </div>
                            <a href="/leads" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                Open Leads module <ArrowUpRight className="h-3 w-3" />
                            </a>
                        </div>
                        <div className="p-5">
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {[
                                    ["new_lead", "New Leads"],
                                    ["contacted", "Contacted"],
                                    ["follow_up", "Follow-up"],
                                    ["proposal_sent", "Proposal"],
                                    ["negotiation", "Negotiation"],
                                    ["won", "Won"],
                                    ["lost", "Lost"],
                                ].map(([key, label]) => (
                                    <div key={key} className="border border-border rounded-md p-3">
                                        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
                                        <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{pipelineCountsWindow[key] || 0}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-4">
                                Lead lists now live in the dedicated Leads module. Use this dashboard tab for quick pipeline status only.
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Two-column: AI Recommendations + activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-md bg-card p-5 flex flex-col" data-testid={DASHBOARD.recommendations}>
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-primary flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> AI Coach
                            </div>
                            <h3 className="font-heading text-xl font-semibold mt-0.5">Today's Recommendations</h3>
                        </div>
                        <button
                            onClick={loadRecs}
                            data-testid={DASHBOARD.recommendationsRefresh}
                            className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${recsLoading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                    {recsLoading && !recs ? (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Analyzing pipeline…</div>
                    ) : recs?.recommendations?.length ? (
                        <ul className="space-y-2 flex-1">
                            {recs.recommendations.map((r, i) => (
                                <li
                                    key={i}
                                    data-testid={DASHBOARD.recommendationItem}
                                    className={`border border-border rounded-md p-3 flex items-start gap-2 hover:bg-secondary/40 transition-colors ${
                                        r.priority === "urgent" ? "border-destructive/40" :
                                        r.priority === "high" ? "border-warning/40" : ""
                                    }`}
                                >
                                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 mt-0.5 border ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.medium}`}>
                                        {r.priority}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium leading-tight">{r.action}</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{r.reason}</div>
                                        {r.target && <div className="text-[10px] font-mono text-primary mt-1">→ {r.target}</div>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">No recommendations right now.</div>
                    )}
                    {recs?.source && (
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-3 pt-2 border-t border-border">
                            Source: {recs.source === "ai" ? "Claude Sonnet 4.5" : "Rule engine"}
                        </div>
                    )}
                </div>
            </div>

            {/* Stat grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <StatCard testId={DASHBOARD.totalLeads} label="Total Leads" value={stats.total_leads} sub={`${stats.active_leads} active`} icon={Users} />
                <StatCard testId={DASHBOARD.followUps} label="Follow-ups" value={stats.pending_followups} sub={`${stats.upcoming_followups} this week`} icon={CalendarClock} accent="bg-warning/10 text-warning" />
                <StatCard testId={DASHBOARD.dealsWon} label="Deals Won" value={stats.won_leads} sub="Total closed" icon={Trophy} accent="bg-success/10 text-success" />
                <StatCard testId={DASHBOARD.activeProjects} label="Active Projects" value={stats.active_projects} sub={`${stats.total_projects} total`} icon={FolderKanban} />
                <StatCard testId={DASHBOARD.revenue} label="Revenue Won" value={`$${(stats.revenue || 0).toLocaleString()}`} sub="Booked" icon={DollarSign} accent="bg-success/10 text-success" />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border rounded-md bg-card p-5" data-testid={DASHBOARD.pipelineChart}>
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Pipeline</div>
                            <h3 className="font-heading text-xl font-semibold mt-0.5">Leads by stage</h3>
                        </div>
                        <a href="/pipeline" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                            View board <ArrowUpRight className="h-3 w-3" />
                        </a>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={allTimePipelineData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                            <XAxis dataKey="stage" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="border border-border rounded-md bg-card p-5">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Lead velocity</div>
                    <h3 className="font-heading text-xl font-semibold mt-0.5 mb-4">Last 6 months</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={stats.monthly_leads}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                            <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                            <Line type="monotone" dataKey="leads" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Recent activity */}
            <div className="border border-border rounded-md bg-card p-5" data-testid={DASHBOARD.recentActivities}>
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Live feed</div>
                        <h3 className="font-heading text-xl font-semibold mt-0.5">Recent activity</h3>
                    </div>
                </div>
                {activities.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">No activity yet.</div>
                ) : (
                    <div>{activities.map((a) => <ActivityRow key={a.id} a={a} />)}</div>
                )}
            </div>

            <AnimatePresence>
                {rescheduleFu && (
                    <RescheduleDialog
                        fu={rescheduleFu}
                        onClose={() => setRescheduleFu(null)}
                        onDone={() => { setRescheduleFu(null); loadToday(); }}
                    />
                )}
                {emailFu && (
                    <EmailDialog
                        fu={emailFu}
                        onClose={() => setEmailFu(null)}
                        onSent={() => { setEmailFu(null); loadToday(); }}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}
